import { ArrayItem, GLTFLoader } from "@babylonjs/loaders/glTF/2.0";
import { BoundingInfo, Geometry, TmpVectors, VertexBuffer } from "@babylonjs/core";
const NAME = "EXT_structural_metadata";
export function IsStructuralMetadata(b) {
    if (b === null || b === undefined || typeof b !== "object")
        return false;
    const obj = b;
    return obj.schema !== undefined;
}
export function IsHasStructuralMetadata(b) {
    if (b === null || b === undefined || typeof b !== "object")
        return false;
    const obj = b;
    return obj.structuralMetadata !== undefined;
}
export class EXT_structural_metadata {
    constructor(loader) {
        this.name = NAME;
        this._metadata = null;
        this._loader = loader;
        this.enabled = this._loader.isExtensionUsed(NAME);
    }
    onLoading() {
        const extensions = this._loader.gltf.extensions;
        if (extensions && extensions[this.name]) {
            const extension = extensions[this.name];
            const babylonObject = this._loader.rootBabylonMesh;
            babylonObject.structuralMetadata = {
                schema: extension.schema,
                propertyTables: extension.propertyTables,
                propertyAttributes: extension.propertyAttributes,
                propertyTextures: extension.propertyTextures,
            };
            this._metadata = babylonObject.structuralMetadata;
        }
    }
    _loadVertexDataAsync(context, primitive, babylonMesh) {
        if (primitive.extensions) {
            const attributes = primitive.attributes;
            if (!attributes) {
                throw new Error(`${context}: Attributes are missing`);
            }
            const babylonGeometry = new Geometry(babylonMesh.name, this._loader._babylonScene);
            const promises = new Array();
            const loadAttribute = (name, kind, callback) => {
                if (attributes[name] == undefined) {
                    return;
                }
                babylonMesh._delayInfo = babylonMesh._delayInfo || [];
                if (babylonMesh._delayInfo.indexOf(kind) === -1) {
                    babylonMesh._delayInfo.push(kind);
                }
                const accessor = ArrayItem.Get(`${context}/attributes/${name}`, this._loader._gltf.accessors, attributes[name]);
                promises.push(this._loader._loadVertexAccessorAsync(`/accessors/${accessor.index}`, accessor, kind).then((babylonVertexBuffer) => {
                    if (babylonVertexBuffer.getKind() === VertexBuffer.PositionKind && !this._loader.parent.alwaysComputeBoundingBox && !babylonMesh.skeleton) {
                        if (accessor.min && accessor.max) {
                            const min = TmpVectors.Vector3[0].copyFromFloats(...accessor.min);
                            const max = TmpVectors.Vector3[1].copyFromFloats(...accessor.max);
                            if (accessor.normalized && accessor.componentType !== 5126) {
                                let divider = 1;
                                switch (accessor.componentType) {
                                    case 5120:
                                        divider = 127.0;
                                        break;
                                    case 5121:
                                        divider = 255.0;
                                        break;
                                    case 5122:
                                        divider = 32767.0;
                                        break;
                                    case 5123:
                                        divider = 65535.0;
                                        break;
                                }
                                const oneOverDivider = 1 / divider;
                                min.scaleInPlace(oneOverDivider);
                                max.scaleInPlace(oneOverDivider);
                            }
                            babylonGeometry._boundingInfo = new BoundingInfo(min, max);
                            babylonGeometry.useBoundingInfoFromGeometry = true;
                        }
                    }
                    babylonGeometry.setVerticesBuffer(babylonVertexBuffer, accessor.count);
                }));
                if (kind == VertexBuffer.MatricesIndicesExtraKind) {
                    babylonMesh.numBoneInfluencers = 8;
                }
                if (callback) {
                    callback(accessor);
                }
            };
            const attributeMappings = [
                ["TEXCOORD_0", VertexBuffer.UVKind, null],
                ["TEXCOORD_1", VertexBuffer.UV2Kind, null],
                ["TEXCOORD_2", VertexBuffer.UV3Kind, null],
                ["TEXCOORD_3", VertexBuffer.UV4Kind, null],
                ["TEXCOORD_4", VertexBuffer.UV5Kind, null],
                ["TEXCOORD_5", VertexBuffer.UV6Kind, null],
                ["POSITION", VertexBuffer.PositionKind, null],
                ["NORMAL", VertexBuffer.NormalKind, null],
                ["TANGENT", VertexBuffer.TangentKind, null],
                ["JOINTS_0", VertexBuffer.MatricesIndicesKind, null],
                ["WEIGHTS_0", VertexBuffer.MatricesWeightsKind, null],
                ["JOINTS_1", VertexBuffer.MatricesIndicesExtraKind, null],
                ["WEIGHTS_1", VertexBuffer.MatricesWeightsExtraKind, null],
                [
                    "COLOR_0",
                    VertexBuffer.ColorKind,
                    (accessor) => {
                        if (accessor.type === "VEC4") {
                            babylonMesh.hasVertexAlpha = true;
                        }
                    },
                ],
            ];
            const extension = primitive.extensions[EXT_structural_metadata.name];
            if (extension && this._metadata) {
                if (extension.propertyAttributes && this._metadata.propertyAttributes) {
                    for (const i of extension.propertyAttributes) {
                        if (i >= 0 && i < this._metadata.propertyAttributes.length) {
                            const ref = this._metadata.propertyAttributes[i];
                            for (const propertyName in ref.properties) {
                                const v = ref.properties[propertyName];
                                attributeMappings.push([v.attribute, v.attribute, null]);
                            }
                        }
                    }
                }
                if (extension.propertyTextures && this._metadata.propertyTextures) {
                    for (const i of extension.propertyTextures) {
                        if (i >= 0 && i < this._metadata.propertyTextures.length) {
                            const ref = this._metadata.propertyTextures[i];
                            for (const propertyName in ref.properties) {
                                const textInfos = ref.properties[propertyName];
                                this._loader.loadTextureInfoAsync(context, textInfos).then((babylonTexture) => {
                                    ref.textures = ref.textures || [];
                                    ref.textures[propertyName] = babylonTexture;
                                });
                            }
                        }
                    }
                }
            }
            attributeMappings.forEach(([attributeName, vertexKind, callback]) => {
                loadAttribute(attributeName, vertexKind, callback == null ? undefined : callback);
            });
            return Promise.all(promises).then(() => {
                return babylonGeometry;
            });
        }
        return null;
    }
    dispose() {
        this._loader = null;
    }
}
GLTFLoader.RegisterExtension(NAME, (loader) => new EXT_structural_metadata(loader));
//# sourceMappingURL=EXT_structural_metadata.js.map